import {
  basesFor,
  chosenIngredients,
  distinctChosen,
  formatCents,
  ingredientGroups,
  ingredientQty,
  newPizza,
  PIZZA_NAME_MAX,
  pizzaCartId,
  pizzaLineName,
  pizzaParts,
  pizzaPriceCents,
  pizzaProblems,
  portionLabel,
  portionPriceCents,
  sizesOf,
  T,
  toggleIngredient,
  tt,
  type BuilderIngredient,
  type PizzaConfig,
  type PizzaDraft,
  type PizzaPart,
} from '@agos/shared'
import { cn } from '@agos/ui'
import { Pizza, X } from 'lucide-react'
import { useState } from 'react'

/**
 * *Vlastná pizza* — FRONTEND.md §9.19.
 *
 * Legacy opens this from the pizza category and the AGOS customer app had no builder at all: the kombo
 * and the burger box were ported, this one was not.
 *
 * **Nothing here decides a price or a name.** Every figure comes from `@agos/shared`'s pizza-builder,
 * the same module the till's own builder uses, moved out of `manager-orders` so both can share it. A
 * pizza that costs one thing at the counter and another on the web is the defect that would otherwise
 * be one copy-paste away — and `pizzaCartId` matters twice over, because the server prices a negative
 * `productId` by bounding it (BACKEND.md §10A.9) and refuses an id it cannot place.
 */
export function CustomPizzaBuilder({
  config,
  ingredients,
  onClose,
  onAdd,
}: {
  config: PizzaConfig | null
  ingredients: readonly BuilderIngredient[]
  onClose: () => void
  onAdd: (line: {
    productId: number
    name: string
    unitPriceCents: number
    parts?: PizzaPart[]
  }) => void
}) {
  // §9.60 — opens on the franchise's default base, or blank where none is set.
  // §9.188 — the ingredients decide the cheese, so they are passed in. Without them the builder
  // opened with no base and no mozzarella, which is what the owner reported.
  const [draft, setDraft] = useState<PizzaDraft>(() => newPizza(config, ingredients))
  /**
   * §9.45 — start on the first size the franchise actually sells, so a franchise offering 40 and 50
   * starts on 40 and nothing is hard-coded.
   *
   * Set during render rather than in an effect, the way §9.40's waiter picker is: `useEffect` for
   * derived state is the cascading-setState the shell's lint rule refuses, and it would also fight
   * somebody who had just chosen a different size. Once, and only while nothing is chosen.
   */
  const firstSize = sizesOf(config)[0]?.cm ?? null
  if (draft.sizeCm === null && firstSize !== null) {
    setDraft((current) => (current.sizeCm === null ? { ...current, sizeCm: firstSize } : current))
  }

  const sizes = sizesOf(config)
  const problems = pizzaProblems(draft, config)
  const chosen = chosenIngredients(draft, ingredients)
  const totalCents = pizzaPriceCents(draft, config, ingredients)

  const add = () => {
    if (problems.length > 0) return
    const name = pizzaLineName(draft, config, ingredients)
    // §10A.14 — the size, the base, every topping and the crust, so the server can cap this line at
    // their sum. They sum to `totalCents` exactly, which is what makes the cap land on the true
    // price rather than refusing an honest basket.
    onAdd({
      productId: pizzaCartId(name),
      name,
      unitPriceCents: totalCents,
      parts: pizzaParts(draft, config, ingredients),
    })
    onClose()
  }

  /* §9.619 - `z-[60]` rather than `z-50`, which is what `BottomNav` carries: equal values leave paint order to
   * decide and the chrome-mounted nav won, so on a phone the bar sat over this sheet's bottom edge - and
   * `items-end` puts the sheet exactly there. 60 is the sibling `KomboBuilder`'s own value, nine lines away in
   * `HomePage`, and the value every other customer bottom sheet is at or above. */
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-background w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          {/* §9.56 - the sentence that says what this screen is for. It was on the entry button and
            * not here, so it appeared on the way in and vanished on arrival. */}
          <div className="flex items-center gap-2.5 min-w-0">
            <Pizza className="w-5 h-5 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="font-bold leading-tight">
                <T>Vlastná pizza</T>
              </p>
              <p className="text-xs text-muted-foreground" data-custom-pizza-subtitle>
                <T>Zložte si pizzu podľa seba</T>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zavrieť')}
            className="p-2.5 rounded-full hover:bg-secondary min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-5 overflow-y-auto flex-1" data-custom-pizza>
          {/* §9.680, owner #17 - *"nech si vieme dať vlastný názov"*. A missed port: legacy's builder opened with
            * this optional field (`Home.tsx:2045-2055`) and §9.19 left it out. The line is written as
            * `Vlastná pizza „<name>“ – 32 cm (...)` by `pizzaLineName`, which also cleans what is typed here. */}
          <label className="block">
            <span className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              <T>Názov pizze</T>
            </span>
            <input
              type="text"
              value={draft.name ?? ''}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              maxLength={PIZZA_NAME_MAX}
              placeholder={tt('Vlastná pizza (voliteľné)')}
              data-pizza-name
              className="w-full px-3 py-2 rounded-xl border-2 border-border text-sm bg-background text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors"
            />
          </label>

          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              <T>Veľkosť</T>
            </p>
            <div className="flex flex-wrap gap-2">
              {sizes.map((size) => (
                <button
                  key={size.cm}
                  type="button"
                  data-pizza-size={size.cm}
                  aria-pressed={draft.sizeCm === size.cm}
                  onClick={() => setDraft({ ...draft, sizeCm: size.cm })}
                  className={cn(
                    'px-3 py-2 rounded-xl border-2 text-sm font-bold transition-colors',
                    draft.sizeCm === size.cm
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/30',
                  )}
                >
                  {size.cm} cm
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              <T>Základ</T>
            </p>
            <div className="flex flex-wrap gap-2">
              {basesFor(config, ingredients).map((base) => (
                <button
                  key={base.value}
                  type="button"
                  data-pizza-base={base.value}
                  aria-pressed={draft.baseValue === base.value}
                  onClick={() => setDraft({ ...draft, baseValue: base.value })}
                  className={cn(
                    'px-3 py-2 rounded-xl border-2 text-sm font-bold transition-colors',
                    draft.baseValue === base.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/30',
                  )}
                >
                  <T>{base.label}</T>
                  {/* §9.679, owner #18a - *"nech neukazuje na webe cenu za základy"*: no price on the chip. The
                    * base is still charged, at its *Pizza ingrediencie* price, and the total below includes it;
                    * `OWNER-QUESTIONS.md` #119 says the web does not print it, the till does. */}
                </button>
              ))}
            </div>
          </div>

          {/* §9.25 - one heading per category, legacy's order, `sortOrder` inside it. This was a
           * flat list in payload order, and it offered the two *Základ* rows as toppings: the
           * pizza's own base, a second time, chargeable on top of the base already chosen. */}
          {ingredientGroups(ingredients).map((group) => (
            <div key={group.category}>
              <p
                className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2"
                data-pizza-ingredient-group={group.category}
              >
                <T>{group.category}</T>
              </p>
              <div className="space-y-1.5">
                {group.ingredients.map((ingredient) => {
                  // §9.678 - 0 up to 3 portions, not a checkbox.
                  const qty = ingredientQty(draft, ingredient.id)
                  const picked = qty > 0
                  // Priced for the chosen size, because an ingredient costs more on a bigger pizza and
                  // showing one number for all of them is how a total stops matching the tiles above it.
                  // §9.678 - and for every portion on it, *"the price multiplied"*: a doubled cheese chip
                  // said one portion's price beside a total that had moved by two.
                  const cents =
                    draft.sizeCm === null ? null : portionPriceCents(ingredient, draft.sizeCm, qty)
                  return (
                    <button
                      key={ingredient.id}
                      type="button"
                      data-pizza-ingredient={ingredient.id}
                      data-pizza-qty={qty}
                      aria-pressed={picked}
                      onClick={() => setDraft(toggleIngredient(draft, ingredient.id))}
                      /* §9.678, owner #18b - **one colour for every chosen count**: *"nech je tam 2x 3x a
                       * nie nech mení farby"*. §9.43 drew a double in orange at the owner's own earlier
                       * request; this supersedes it, and the count below is what tells them apart. */
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 rounded-xl border text-left text-sm transition-colors',
                        qty === 0 ? 'border-border hover:bg-muted' : 'border-primary bg-primary/5',
                      )}
                    >
                      {/* The count rather than a tick, and large: *2×* and *3×* are what the owner asked to
                        * see, and at 10px it was the one thing on the chip nobody could read. */}
                      <span
                        data-pizza-portions
                        className={cn(
                          'min-w-[2.25rem] h-7 px-1 rounded-lg border-2 flex items-center justify-center shrink-0 text-base font-extrabold tabular-nums',
                          qty === 0
                            ? 'border-border text-muted-foreground'
                            : 'bg-primary border-primary text-primary-foreground',
                        )}
                      >
                        {qty === 0 ? '+' : portionLabel(qty)}
                      </span>
                      <span className="flex-1 min-w-0 truncate" data-pizza-ingredient-name>
                        <T>{ingredient.name}</T>
                      </span>
                      {cents !== null && (
                        <span className="text-xs text-muted-foreground shrink-0" data-pizza-ingredient-price>
                          +{formatCents(cents)}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}

          {/* §9.93 - no crust control. An `Okraj` row is an ordinary topping now, offered with
            * the rest above: the owner asked for *"the same logic as other ingrediencies… you can
            * select basically both of them"*, and one-of controls cannot say that. */}

          {problems.map((problem) => (
            <p key={problem} className="text-xs text-amber-500" data-pizza-problem>
              {problem}
            </p>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-border shrink-0 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">
              <T>Spolu</T>
            </p>
            <p className="font-bold" data-pizza-total>
              {formatCents(totalCents)}
            </p>
            {chosen.length > 0 && (
              <p className="text-[11px] text-muted-foreground truncate" data-pizza-chosen>
                {/* §9.678 - each topping once with its count, as the line writes it. This listed every
                  * portion, so a tripled cheese read *Syr, Syr, Syr* under a chip that says *3×*. */}
                {distinctChosen(draft, ingredients)
                  .map(({ ingredient, qty }) => (qty > 1 ? `${portionLabel(qty)} ${ingredient.name}` : ingredient.name))
                  .join(', ')}
              </p>
            )}
          </div>
          <button
            type="button"
            data-pizza-add
            disabled={problems.length > 0}
            onClick={add}
            className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm disabled:opacity-40"
          >
            <T>Pridať do košíka</T>
          </button>
        </div>
      </div>
    </div>
  )
}
